import moment from 'moment'
import { Countries } from '../models/countries'
import { format } from '../models/format'

const keepNumbersOnly = input => (input || '').replace(/[\D]/g, '')

const checkMod11 = input => {
  const numbers = keepNumbersOnly(input).split('')
  const values = [4, 3, 2, 7, 6, 5, 4, 3, 2]
  const control = numbers
    .slice(0, -1)
    .reduce((prev, curr, currIndex) => prev + curr * values[currIndex], 0)
  return 11 - (control % 11) === +numbers.slice(-1)[0]
}

const parseDateDKFO = governmentId => {
  const [d1, d2, m1, m2, y3, y4] = keepNumbersOnly(governmentId)
  const date = moment(
    `${(moment().year() / 100).toFixed(0)}${y3}${y4}-${m1}${m2}-${d1}${d2}`,
  )
  if (date > moment()) {
    date.subtract(100, 'year')
  }
  return date
}

const validateDKFONaturalGovernmentId = input => {
  const foCandidate = keepNumbersOnly(input)
  return foCandidate.length === 9 && checkMod11(`0${foCandidate}`)
}

const parseDateDK = governmentId => {
  if (validateDKFONaturalGovernmentId(governmentId)) {
    return parseDateDKFO(governmentId)
  }
  const [d1, d2, m1, m2, y3, y4, c] = keepNumbersOnly(governmentId)
  const y3y4 = y3 * 10 + +y4
  let y1y2
  switch (true) {
    case c <= 3:
      y1y2 = 19
      break
    case +c === 4 || +c === 9:
      y1y2 = y3y4 <= 36 ? 20 : 19
      break
    case c >= 5 && c <= 8:
      y1y2 = y3y4 <= 57 ? 20 : 18
      break
    default:
      break
  }
  return moment(`${y1y2}${y3}${y4}-${m1}${m2}-${d1}${d2}`)
}

const parseDateSE = governmentId => {
  if (governmentId.length > 11) {
    return moment(governmentId, 'YYYYMMDD')
  }
  const date = moment(
    (moment().year() / 100).toFixed(0) + governmentId,
    'YYYYMMDD',
  )
  if (date > moment()) {
    date.subtract(100, 'year')
  }
  if (governmentId.includes('+') && moment().diff(date, 'year') < 100) {
    date.subtract(100, 'year')
  }
  return date
}

const parseDateNO = governmentId => {
  const [d1, d2, m1, m2, y3, y4, c1, c2, c3] = keepNumbersOnly(governmentId)
  const y3y4 = y3 * 10 + +y4
  const i = +(c1 + c2 + c3)
  let y1y2
  switch (true) {
    case i <= 499:
      y1y2 = 19
      break
    case y3y4 >= 40 && y3y4 <= 99 && i >= 900:
      y1y2 = 19
      break
    case y3y4 <= 39 && i >= 500:
      y1y2 = 20
      break
    case y3y4 >= 54 && y3y4 <= 99 && i >= 500 && i <= 749:
      y1y2 = 18
      break
    default:
      break
  }
  return moment(`${y1y2}${y3}${y4}-${m1}${m2}-${d1 % 4}${d2}`)
}

const parseDateFI = governmentId => {
  const [d1, d2, m1, m2, y3, y4, c] = governmentId
  let y1y2
  switch (c) {
    case '+':
      y1y2 = 18
      break
    case '-':
      y1y2 = 19
      break
    case 'A':
      y1y2 = 20
      break
    default:
      break
  }
  return moment(`${y1y2}${y3}${y4}-${m1}${m2}-${d1}${d2}`)
}

const parseDate = (governmentId = '', country = '') => {
  switch (country) {
    case Countries.DK:
      return parseDateDK(governmentId)
    case Countries.SE:
      return parseDateSE(governmentId)
    case Countries.NO:
      return parseDateNO(governmentId)
    case Countries.FI:
      return parseDateFI(governmentId)
    default:
      return moment()
  }
}

const isOldEnough = (date, country) => {
  const age = moment().diff(date, 'years')
  switch (country) {
    case Countries.SE:
    case Countries.DK:
    case Countries.FI:
    case Countries.NO:
    default:
      return age >= 18
  }
}

const getGovernmentIdDateFormatByCountry = country => {
  switch (country) {
    case Countries.SE:
      return ['YYMMDD', 'YYYYMMDD']
    case Countries.NO:
    case Countries.DK:
    case Countries.FI:
      return ['DDMMYY', 'DDMMYYYY']
    default:
      return ['YYMMDD', 'YYYYMMDD']
  }
}

export const governmentIdToDate = (governmentId, country) => {
  const dateFromId = governmentId
    .substring(0, governmentId.length - 4)
    .replace('-', '')

  if (dateFromId.length === 6) {
    const century =
      Number(dateFromId.substring(0, 2)) >=
      Number(moment().year().toString().substring(2, 4))
        ? '19'
        : '20'
    if (country === Countries.SE) {
      return century + dateFromId
    }
    return dateFromId.substring(0, 4) + century + dateFromId.substring(4)
  }
  return dateFromId
}

export const validateAge = (countryCode, governmentId) => {
  return isOldEnough(parseDate(governmentId, countryCode))
}

const checkLuhn = input => {
  const numdigits = input.length
  const parity = numdigits % 2
  let sum = 0
  let i
  let digit

  for (i = 0; i < numdigits; i += 1) {
    digit = parseInt(input.charAt(i), 10)
    if (i % 2 === parity) {
      digit *= 2
    }
    if (digit > 9) {
      digit -= 9
    }
    sum += digit
  }

  return sum % 10 === 0
}

export const validateDate = (governmentId, countryCode) => {
  const formats = getGovernmentIdDateFormatByCountry(countryCode)
  return governmentId
    ? moment(governmentIdToDate(governmentId, countryCode), formats).isValid()
    : false
}

export const validateSENaturalGovernmentId = input => {
  if (!input) {
    return false
  }

  let modifiedInput = keepNumbersOnly(input)

  if (modifiedInput.length === 12) {
    modifiedInput = modifiedInput.substring(2)
  }

  if (!modifiedInput.match(/^(\d{10})$/)) {
    return false
  }

  if (!validateDate(modifiedInput, Countries.SE)) return false

  return checkLuhn(modifiedInput)
}

export const validateSEGovernmentId = (input = '') => {
  const modifiedInput = (/(.{10})$/.exec(keepNumbersOnly(input)) || [])[1]
  return modifiedInput && checkLuhn(modifiedInput)
}

export const validateFINaturalGovernmentId = input => {
  if (!input || input.length !== 11) {
    return false
  }

  if (!new RegExp(format.governmentId.FI.regex).test(input)) {
    return false
  }

  if (!moment(input.substring(0, 6), 'DDMMYY').isValid()) {
    return false
  }

  // prettier-ignore
  const controlChars = ['0','1','2','3','4','5','6','7','8','9','A','B','C','D','E','F','H','J','K','L','M','N','P','R','S','T','U','V','W','X','Y',]

  const controlCharIndex = keepNumbersOnly(input.substring(0, 10)) % 31
  return controlChars[controlCharIndex] === input[10]
}

export const validateNONaturalGovernmentId = input => {
  if (!input || input.length !== 11) {
    return false
  }

  if (!new RegExp(format.governmentId.NO.regex).test(input)) {
    return false
  }

  const [d1, d2, m1, m2, y1, y2, i1, i2, i3, i4, i5] = input

  if (d1 <= 3 && !moment(input.substring(0, 6), 'DDMMYY').isValid()) {
    return false
  }
  if (d1 >= 4 && !moment(d1 - 4 + input.substring(1, 6), 'DDMMYY').isValid()) {
    return false
  }

  // prettier-ignore
  const k1 = 11 - ((3 * d1 + 7 * d2 + 6 * m1 + 1 * m2 + 8 * y1 + 9 * y2 + 4 * i1 + 5 * i2 + 2 * i3) % 11)
  // prettier-ignore
  const k2 = 11 - ((5 * d1 + 4 * d2 + 3 * m1 + 2 * m2 + 7 * y1 + 6 * y2 + 5 * i1 + 4 * i2 + 3 * i3 + 2 * k1) % 11)

  return +i4 === k1 % 11 && +i5 === k2 % 11
}

export const validateDKNaturalGovernmentId = input => {
  if (validateDKFONaturalGovernmentId(input)) {
    return true
  }
  return (
    new RegExp(format.governmentId.DK.regex).test(input || '') &&
    moment(input.substring(0, 6), 'DDMMYY').isValid()
  )
}

export const validateNaturalGovernmentId = (countryCode, govId) => {
  switch (countryCode) {
    case Countries.DK:
      return validateDKNaturalGovernmentId(govId)
    case Countries.NO:
      return validateNONaturalGovernmentId(govId)
    case Countries.FI:
      return validateFINaturalGovernmentId(govId)
    case Countries.SE:
      return validateSENaturalGovernmentId(govId)
    default:
      return false
  }
}

export const naturalGovIdPlaceholder = (countryCode, language) => {
  switch (countryCode) {
    case Countries.DK:
      return 'ddmmåå-nnnn'
    case Countries.NO:
      return 'ddmmåånnnnn'
    case Countries.FI:
      return (language === 'fi' && 'PPKKVVYNNNT') || 'ddmmååsnnnk'
    case Countries.SE:
      return 'ååååmmdd-nnnn'
    default:
      return ''
  }
}
